/**
 * The presentations waiting on the signed-in reviewer, and the two places they are told about
 * them: an alert on the front door when they sign in, and a count in the top bar on every page.
 *
 * Nothing is stored to make this work. A deck sent for review is In review and assigned to its
 * reviewer, so their queue is the decks list asked for exactly that — and it empties itself as
 * they approve or reject, because a decision assigns the deck back to whoever sent it.
 */
import { useState } from 'react';
import RateReviewRounded from '@mui/icons-material/RateReviewRounded';
import {
  Alert,
  AlertTitle,
  Badge,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { keys, presentations } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthProvider';
import { formatDateTime } from '@/components/common';
import type { PresentationDeck } from '@/types';

const QUEUE = { assignedToMe: true, reviewStatus: 'In review', pageSize: 50 } as const;

/** Where a deck waiting on review opens: its Review step, with Approve and Reject on it. */
export function reviewLink(deck: PresentationDeck): string {
  return `/presentations/decks/${deck.id}?step=review`;
}

/** The decks waiting on this user's review; none, without asking, for anyone who does not review. */
export function useReviewQueue(): PresentationDeck[] {
  const { can } = useAuth();
  const reviews = can('presentation:review');
  const query = useQuery({
    queryKey: keys.presentationDecks(QUEUE),
    queryFn: () => presentations.list({ ...QUEUE }),
    enabled: reviews,
    // A deck can be sent while the reviewer is signed in; look again now and then.
    refetchInterval: 120_000,
  });
  return reviews ? (query.data?.items ?? []) : [];
}

function waiting(count: number): string {
  return count === 1 ? '1 presentation is waiting for your review' : `${count} presentations are waiting for your review`;
}

function sentBy(deck: PresentationDeck): string {
  return `${deck.clientName ?? 'Employer'} · sent by ${deck.submittedByName ?? 'someone no longer listed'} ${formatDateTime(deck.submittedAt)}`;
}

/** The front door's alert: what is waiting, each with a link straight to its review. */
export function ReviewQueueAlert(): JSX.Element | null {
  const decks = useReviewQueue();
  if (decks.length === 0) return null;
  return (
    <Alert severity="info" icon={<RateReviewRounded />}>
      <AlertTitle>{waiting(decks.length)}</AlertTitle>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
        {decks.map((deck) => (
          <li key={deck.id}>
            <Link component={RouterLink} to={reviewLink(deck)} sx={{ fontWeight: 600 }}>
              {deck.name}
            </Link>{' '}
            — {sentBy(deck)}
          </li>
        ))}
      </Stack>
    </Alert>
  );
}

/** The top bar's count, on every page; it opens the same list. Nothing shows when nothing waits. */
export function ReviewQueueButton(): JSX.Element | null {
  const decks = useReviewQueue();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (decks.length === 0) return null;
  return (
    <>
      <Tooltip title={waiting(decks.length)}>
        <IconButton aria-label={waiting(decks.length)} onClick={(event) => setAnchor(event.currentTarget)}>
          <Badge badgeContent={decks.length} color="primary">
            <RateReviewRounded />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {decks.map((deck) => (
          <MenuItem
            key={deck.id}
            onClick={() => {
              setAnchor(null);
              navigate(reviewLink(deck));
            }}
          >
            <ListItemText primary={deck.name} secondary={sentBy(deck)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
